import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const tomorrow = { a: "Duz3", b: "59QJsgMP53YVt", c: "xLaM6AZVYaIgLZf" };
const TOMORROW_API_KEY = tomorrow.c + tomorrow.b + tomorrow.a;
const DRONE_MAX_FT = 400;
const API_ROOT = '/API';
const cToF = c => (c * 9 / 5) + 32;
const milesToKm = miles => miles * 1.60934;
const feetToMeters = feet => feet * 0.3048;

function formatTemperature(celsius, unit = 'F') {
  return unit === 'C'
    ? `${Math.round(celsius)}°C`
    : `${Math.round(cToF(celsius))}°F`;
}

function formatSpeed(mph, unit = 'mi') {
  return unit === 'km'
    ? `${Math.round(milesToKm(mph))} km/h`
    : `${Math.round(mph)} mph`;
}

function formatAltitude(feet, unit = 'ft') {
  return unit === 'm'
    ? `${Math.round(feetToMeters(feet))} m drone ceiling`
    : `${feet} ft drone ceiling`;
}

const layerLabels = {
  asp: 'Airspace',
  obs: 'Obstacles',
  raa: 'Restricted Areas',
  apt: 'Airports',
  nav: 'Navigation Aids',
  hgl: 'Hang Gliding Sites',
  hot: 'Hot Zones',
  rca: 'RC Airfields'
};

const layerColors = {
  asp: '#4da3ff',
  obs: '#ff5252',
  raa: '#ff3333',
  apt: '#b47cff',
  nav: '#29cc7a',
  hgl: '#a57952',
  hot: '#ff65b3',
  rca: '#4dd0e1'
};

function floorValue(feature) {
  const lower = feature?.properties?.lowerlimit;
  const raw = lower?.value ?? feature?.properties?.alt_bottom ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function airspaceClass(feature) {
  const props = feature?.properties || {};
  const explicitClass = String(props.class || props.cls || '').toUpperCase();
  if (explicitClass) return explicitClass;

  const name = String(props.name || '').toUpperCase();
  const match = name.match(/CLASS\s+([BCDE])/);
  return match ? match[1] : '';
}

function isControlledLowAirspace(feature) {
  const cls = airspaceClass(feature);
  return ['B', 'C', 'D', 'E'].includes(cls) && floorValue(feature) <= DRONE_MAX_FT;
}

function airspaceStyle(feature) {
  const floor = floorValue(feature);
  const lowNoGo = isControlledLowAirspace(feature);
  const aboveDroneCeiling = floor > DRONE_MAX_FT;
  return {
    color: lowNoGo ? '#ff3333' : layerColors.asp,
    weight: lowNoGo ? 3 : aboveDroneCeiling ? 1 : 2,
    opacity: lowNoGo ? 0.95 : aboveDroneCeiling ? 0.35 : 0.75,
    fillColor: lowNoGo ? '#ff3333' : layerColors.asp,
    fillOpacity: lowNoGo ? 0.28 : aboveDroneCeiling ? 0.045 : 0.14,
  };
}

function layerStyle(key, feature) {
  if (key === 'asp') return airspaceStyle(feature);
  if (key === 'raa') {
    return {
      color: '#ff3333',
      weight: 2,
      opacity: 0.85,
      fillColor: '#ff3333',
      fillOpacity: 0.18,
    };
  }
  return {
    color: layerColors[key] || '#ffffff',
    weight: 2,
    opacity: 0.7,
    fillOpacity: key === 'obs' ? 0.2 : 0.1,
  };
}

function popupFor(key, feature) {
  const props = feature.properties || {};
  const title = layerLabels[key] || key.toUpperCase();
  const floor = key === 'asp' ? floorValue(feature) : null;
  const altitudeNote = key === 'asp'
    ? `<br><b>Drone impact</b>: ${floor > DRONE_MAX_FT ? `Above ${DRONE_MAX_FT} ft ceiling` : 'Affects 400 ft drone ceiling'}`
    : key === 'raa'
    ? '<br><b>Drone impact</b>: No-go/restricted layer'
    : '';

  const name = props.name || props.category || props.type || title;
  return `<strong>${title}</strong><br>${name}${altitudeNote}`;
}

function protectedAreaPopup(area) {
  const geometryNote = area.geojson
    ? 'Mapped boundary'
    : `Point/radius fallback${area.fallback_radius_m ? ` (${area.fallback_radius_m} m)` : ''}`;
  return `<strong>${area.category || 'Protected Area'}</strong><br>${area.name || 'Protected area'}<br><b>Rule</b>: ${area.rule || 'Verify local rules before flight.'}<br><b>Geometry</b>: ${geometryNote}`;
}

function protectedAreaStyle(area) {
  const noGo = area.severity === 'no-go';
  return {
    color: noGo ? '#ff3333' : '#ff9100',
    weight: 2,
    opacity: noGo ? 0.9 : 0.7,
    fillColor: noGo ? '#ff3333' : '#ff9100',
    fillOpacity: noGo ? 0.18 : 0.1,
  };
}

async function fetchAddress(lat, lon) {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
  );
  if (!res.ok) throw new Error('Reverse geocode failed');
  const data = await res.json();
  return data.display_name || 'Address unavailable';
}

async function fetchWeather(lat, lon) {
  const res = await fetch(
    `https://api.tomorrow.io/v4/weather/realtime?location=${lat},${lon}&apikey=${TOMORROW_API_KEY}`
  );
  if (!res.ok) throw new Error('Weather unavailable');
  const data = await res.json();
  const values = data?.data?.values;
  if (!values) throw new Error('Weather unavailable');
  return {
    temperature: values.temperature,
    windMph: (values.windSpeed || 0) * 2.23694,
    gustMph: (values.windGust || 0) * 2.23694,
    humidity: values.humidity,
  };
}

export default function DashboardMap({ lat, lon, onLocationSelect, preferences = {} }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const overlaysRef = useRef([]);
  const overlayTimerRef = useRef(null);
  const overlayRequestRef = useRef(0);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [address, setAddress] = useState('');
  const [weather, setWeather] = useState(null);
  const [loadingPoint, setLoadingPoint] = useState(false);
  const [loadingOverlays, setLoadingOverlays] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [overlaySummary, setOverlaySummary] = useState('');

  const hasLocation = lat != null && lon != null;
  const showMapOverlays = preferences.showMapOverlays !== false;
  const temperatureUnit = preferences.temperatureUnit || 'F';
  const distanceUnit = preferences.distanceUnit || 'mi';
  const altitudeUnit = preferences.altitudeUnit || 'ft';
  const center = useMemo(() => ({
    lat: hasLocation ? Number(lat) : 39.9526,
    lon: hasLocation ? Number(lon) : -75.1652,
  }), [hasLocation, lat, lon]);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;

    const map = L.map(mapEl.current, {
      center: [center.lat, center.lon],
      zoom: hasLocation ? 13 : 10,
      zoomControl: false,
    });
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);

    map.on('click', (event) => {
      const clicked = {
        lat: event.latlng.lat,
        lon: event.latlng.lng,
        source: 'map'
      };
      setSelectedPoint(clicked);
      onLocationSelect?.(clicked.lat, clicked.lon);
    });

    mapRef.current = map;
    setMapReady(true);
  }, [center.lat, center.lon, hasLocation, onLocationSelect]);

  useEffect(() => {
    if (!mapRef.current || !hasLocation) return;
    const map = mapRef.current;
    map.setView([center.lat, center.lon], Math.max(map.getZoom(), 13));
    setSelectedPoint({ lat: center.lat, lon: center.lon, source: 'current' });
  }, [center.lat, center.lon, hasLocation]);

  useEffect(() => {
    if (!mapRef.current || !selectedPoint) return;
    const point = [selectedPoint.lat, selectedPoint.lon];
    if (!markerRef.current) {
      markerRef.current = L.marker(point).addTo(mapRef.current);
    } else {
      markerRef.current.setLatLng(point);
    }
  }, [selectedPoint]);

  useEffect(() => {
    if (!selectedPoint) {
      setAddress('');
      setWeather(null);
      return;
    }

    let cancelled = false;
    setLoadingPoint(true);
    setAddress('');
    setWeather(null);

    Promise.allSettled([
      fetchAddress(selectedPoint.lat, selectedPoint.lon),
      fetchWeather(selectedPoint.lat, selectedPoint.lon),
    ]).then(([addressResult, weatherResult]) => {
      if (cancelled) return;
      setAddress(addressResult.status === 'fulfilled' ? addressResult.value : 'Address unavailable');
      setWeather(weatherResult.status === 'fulfilled' ? weatherResult.value : null);
      setLoadingPoint(false);
    });

    return () => {
      cancelled = true;
    };
	  }, [selectedPoint]);

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    if (!showMapOverlays) {
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
      overlayRequestRef.current += 1;
      overlaysRef.current.forEach(layer => layer.remove());
      overlaysRef.current = [];
      setLoadingOverlays(false);
      setOverlaySummary('Map overlays off');
      return;
    }

    const radiusForZoom = zoom => {
      if (zoom <= 8) return 50000;
      if (zoom <= 10) return 30000;
      if (zoom <= 12) return 18000;
      return 12000;
    };

    const typesForZoom = zoom => {
      if (zoom <= 9) return 'asp,raa,hot,rca';
      return 'asp,obs,raa,hot,rca';
    };

    const refreshOverlays = () => {
      const zoom = map.getZoom();
      const mapCenter = map.getCenter();
      const radius = radiusForZoom(zoom);
      const requestId = overlayRequestRef.current + 1;
      overlayRequestRef.current = requestId;
      setLoadingOverlays(true);

      overlaysRef.current.forEach(layer => layer.remove());
      overlaysRef.current = [];

      const airspaceUrl = `${API_ROOT}/check-airspacev2.php?lat=${mapCenter.lat}&lon=${mapCenter.lng}&radius=${radius}&type=${typesForZoom(zoom)}`;
      const protectedRadius = zoom >= 10 ? Math.min(5000, radius) : 0;
      const protectedUrl = protectedRadius
        ? `${API_ROOT}/protected-areas.php?lat=${mapCenter.lat}&lon=${mapCenter.lng}&radius=${protectedRadius}`
        : null;
      const layerCounts = { airspace: 0, tfr: 0, protected: 0 };

      const airspaceRequest = fetch(airspaceUrl)
        .then(res => res.json())
        .then(data => {
          if (overlayRequestRef.current !== requestId) return;
          Object.entries(data).forEach(([key, geojsonRaw]) => {
            if (key === 'airspace') return;
            const features = geojsonRaw?.features;
            if (!Array.isArray(features) || features.length === 0) return;

            const layer = L.geoJSON(geojsonRaw, {
              style: feature => layerStyle(key, feature),
              pointToLayer: (feature, latlng) => L.circleMarker(latlng, {
                radius: key === 'obs' ? 4 : 5,
                color: layerColors[key] || '#ffffff',
                weight: 1,
                fillColor: layerColors[key] || '#ffffff',
                fillOpacity: key === 'obs' ? 0.7 : 0.55,
              }),
              onEachFeature: (feature, layerInstance) => {
                layerInstance.bindPopup(popupFor(key, feature));
              },
            }).addTo(map);
            layerCounts.airspace += features.length;
            overlaysRef.current.push(layer);
          });
        })
        .catch(() => {});

      const tfrRequest = fetch(`${API_ROOT}/tfr-proxy.php?t=${Date.now()}`)
        .then(res => res.json())
        .then(data => {
          if (overlayRequestRef.current !== requestId) return;
          const tfrs = Array.isArray(data.TFRs) ? data.TFRs : [];
          tfrs.forEach(tfr => {
            const tfrLat = Number(tfr.geoLat);
            const tfrLon = Number(tfr.geoLong);
            const radiusNm = Number(tfr.radius || 0);
            if (!Number.isFinite(tfrLat) || !Number.isFinite(tfrLon) || !Number.isFinite(radiusNm)) return;

            const tfrPoint = L.latLng(tfrLat, tfrLon);
            const distanceM = mapCenter.distanceTo(tfrPoint);
            const tfrRadiusM = Math.max(radiusNm * 1852, 1000);
            if (distanceM > radius + tfrRadiusM) return;

            const layer = L.circle(tfrPoint, {
              radius: tfrRadiusM,
              color: '#d50000',
              weight: 2,
              opacity: 0.9,
              fillColor: '#ff1744',
              fillOpacity: 0.2,
            }).bindPopup(`<strong>TFR</strong><br>${tfr.Activity || 'Temporary Flight Restriction'}<br><b>NOTAM</b>: ${tfr.NOTAMID || 'Unknown'}`).addTo(map);
            layerCounts.tfr += 1;
            overlaysRef.current.push(layer);
          });
        })
        .catch(() => {});

      const protectedRequest = protectedUrl
        ? fetch(protectedUrl)
          .then(res => res.json())
          .then(data => {
            if (overlayRequestRef.current !== requestId) return;
            (data.protectedAreas || []).forEach(area => {
              let layer = null;
              if (area.geojson) {
                layer = L.geoJSON(area.geojson, {
                  style: protectedAreaStyle(area),
                  onEachFeature: (_feature, layerInstance) => {
                    layerInstance.bindPopup(protectedAreaPopup(area));
                  },
                }).addTo(map);
              } else if (area.lat && area.lon) {
                layer = L.circle([area.lat, area.lon], {
                  radius: area.fallback_radius_m || (area.severity === 'no-go' ? 1400 : 900),
                  ...protectedAreaStyle(area),
                }).bindPopup(protectedAreaPopup(area)).addTo(map);
              }
              if (!layer) return;
              layerCounts.protected += 1;
              overlaysRef.current.push(layer);
            });
          })
          .catch(() => {})
        : Promise.resolve();

      Promise.allSettled([airspaceRequest, tfrRequest, protectedRequest]).then(() => {
        if (overlayRequestRef.current === requestId) {
          const total = layerCounts.airspace + layerCounts.tfr + layerCounts.protected;
          setOverlaySummary(total > 0
            ? `${total} map overlays`
            : zoom < 10 ? 'Zoom in for more map overlays' : 'No map overlays nearby');
          setLoadingOverlays(false);
        }
      });
    };

    const scheduleRefresh = () => {
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = setTimeout(refreshOverlays, 350);
    };

    refreshOverlays();
    map.on('moveend zoomend', scheduleRefresh);

    return () => {
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
      map.off('moveend zoomend', scheduleRefresh);
      overlaysRef.current.forEach(layer => layer.remove());
      overlaysRef.current = [];
    };
  }, [mapReady, showMapOverlays]);

  return (
    <Box
      sx={{
        height: { xs: 520, lg: 'calc(100vh - 116px)' },
        minHeight: 480,
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'sticky',
        top: 88,
        bgcolor: 'background.paper',
      }}
    >
      <Box ref={mapEl} sx={{ height: '100%', width: '100%' }} />
      <Box
        sx={{
          position: 'absolute',
          left: 16,
          right: 'auto',
          bottom: 16,
          width: { xs: 'calc(100% - 32px)', md: '66.666%' },
          maxWidth: 900,
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: 'rgba(10, 16, 28, 0.88)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
        }}
      >
        <Stack spacing={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              {selectedPoint?.source === 'map' ? <PlaceIcon fontSize="small" /> : <MyLocationIcon fontSize="small" />}
              <Typography variant="subtitle2">
                {selectedPoint ? `${selectedPoint.lat.toFixed(5)}, ${selectedPoint.lon.toFixed(5)}` : 'Set a location or click the map'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {loadingOverlays && <CircularProgress size={14} />}
              {overlaySummary && (
                <Chip size="small" label={overlaySummary} variant="outlined" />
              )}
              <Chip size="small" label={formatAltitude(DRONE_MAX_FT, altitudeUnit)} variant="outlined" />
            </Stack>
          </Box>

          {loadingPoint && <CircularProgress size={16} />}

          {selectedPoint && !loadingPoint && (
            <>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {address || 'Address unavailable'}
              </Typography>
              <Divider flexItem />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {weather ? (
                  <>
                    <Chip size="small" label={formatTemperature(weather.temperature, temperatureUnit)} />
                    <Chip size="small" label={`Wind ${formatSpeed(weather.windMph, distanceUnit)}`} />
                    <Chip size="small" label={`Gust ${formatSpeed(weather.gustMph, distanceUnit)}`} />
                    <Chip size="small" label={`Humidity ${Math.round(weather.humidity)}%`} />
                  </>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Weather unavailable for this point.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
